/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Form, FormInstance } from 'antd';

import { NodeOperatingSystem } from '@/client';
import { ValidationError } from '@/components/Forms/ValidationError';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NewFleetFormFields, FLEET_NAME_MAX_LENGTH } from '@/pages/FleetManagement/NewFleetWizardSteps/types';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { Text } from '@/primitives/Text';
import { TextArea } from '@/primitives/TextArea';

export type FleetDetailsStepProps = {
  form: FormInstance<NewFleetFormFields>;
  operatingSystem: NodeOperatingSystem;
  isPending: boolean;
};

export function FleetDetailsStep({ form, operatingSystem, isPending }: FleetDetailsStepProps) {
  const { t } = useMimicTranslation('nodes');

  return (
    <div className="create-fleet-form">
      <Form form={form} layout="vertical" validateTrigger="onBlur" style={{ width: '100%' }}>
        <Form.Item<NewFleetFormFields>
          label={t('createFleetWizard.details.name')}
          name="name"
          rules={[
            {
              required: true,
              message: <ValidationError>{t('createFleetWizard.details.nameRequired')}</ValidationError>,
            },
            {
              max: FLEET_NAME_MAX_LENGTH,
              message: (
                <ValidationError>
                  {t('createFleetWizard.details.nameMaxLength', { max: FLEET_NAME_MAX_LENGTH })}
                </ValidationError>
              ),
            },
          ]}
        >
          <Input
            placeholder={t('createFleetWizard.details.namePlaceholder')}
            disabled={isPending}
            count={{ max: FLEET_NAME_MAX_LENGTH }}
            style={{ backgroundColor: '#ffffff' }}
          />
        </Form.Item>

        <Form.Item<NewFleetFormFields> label={t('createFleetWizard.details.description')} name="description">
          <TextArea
            allowClear
            rows={3}
            maxLength={280}
            count={{ show: true, max: 280 }}
            style={{ backgroundColor: '#ffffff' }}
          />
        </Form.Item>

        <Form.Item label={t('createFleetWizard.details.operatingSystem')}>
          <Flex align="center" gap="xs">
            <OsIcon name={operatingSystem} />
            <Text>
              {operatingSystem === 'windows'
                ? t('createFleetWizard.details.osWindows')
                : t('createFleetWizard.details.osLinux')}
            </Text>
          </Flex>
        </Form.Item>
      </Form>
    </div>
  );
}
